import React, { useState, useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import { recommendedProjectsAtom, addRecentProjectIdAtom } from '../../../../atoms/v3/recentProjectsAtom';
import { Project } from '../../../../types/project';

interface ProjectSelectorProps {
  selectedProjectId: string | null;
  onSelectProject: (projectId: string | null) => void;
  onClose?: () => void;
}

/**
 * 项目选择器组件
 * 显示推荐项目和所有项目的下拉菜单
 */
export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  selectedProjectId,
  onSelectProject,
  onClose
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const [allProjects] = useAtom(projectsAtom);
  const [recommendedProjects] = useAtom(recommendedProjectsAtom);
  const [, addRecentProjectId] = useAtom(addRecentProjectIdAtom);

  // 下拉菜单状态
  const [isOpen, setIsOpen] = useState(false);
  const [menuMounted, setMenuMounted] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  // 处理点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 处理项目选择
  const handleSelectProject = (project: Project | null) => {
    // 立即调用父组件的回调函数，不要延迟
    onSelectProject(project?.id || null);

    // 如果选择了项目，添加到最近使用列表
    if (project) {
      addRecentProjectId(project.id);
    }

    // 记录选择事件，便于调试
    console.log('[ProjectSelector] Selected project:', project?.title || 'No Project');

    // 关闭菜单
    setIsOpen(false);
  };

  // 处理省略号按钮点击
  const handleMoreClick = () => {
    setIsOpen(!isOpen);
  };

  // 处理菜单挂载状态
  useEffect(() => {
    if (isOpen) {
      // 菜单打开时，设置为已挂载
      setMenuMounted(true);
    } else {
      // 菜单关闭后，延迟重置挂载状态，确保动画完成
      const timer = setTimeout(() => {
        setMenuMounted(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // 获取当前选中项目的信息
  const selectedProject = selectedProjectId ? allProjects.find(p => p.id === selectedProjectId) : null;

  // 准备要显示的项目列表
  const getDisplayProjects = () => {
    // 过滤出有效的推荐项目
    const validRecommendedProjects = recommendedProjects.filter((project): project is Project => !!project);

    // 如果没有选中项目
    if (!selectedProject) {
      // 如果推荐项目不足4个，从所有项目中补充
      if (validRecommendedProjects.length < 4) {
        const additionalProjects = allProjects
          .filter(project => !validRecommendedProjects.some(p => p.id === project.id))
          .slice(0, 4 - validRecommendedProjects.length);

        return [...validRecommendedProjects, ...additionalProjects].slice(0, 4);
      }

      // 如果推荐项目足够，直接返回前4个
      return validRecommendedProjects.slice(0, 4);
    }

    // 如果有选中项目，确保它在第一位
    const otherRecommendedProjects = validRecommendedProjects
      .filter(project => project.id !== selectedProjectId)
      .slice(0, 3); // 取三个其他推荐项目，加上选中项目共四个

    // 如果其他推荐项目不足3个，从所有项目中补充
    if (otherRecommendedProjects.length < 3) {
      const additionalProjects = allProjects
        .filter(project =>
          project.id !== selectedProjectId &&
          !otherRecommendedProjects.some(p => p.id === project.id)
        )
        .slice(0, 3 - otherRecommendedProjects.length);

      return [selectedProject, ...otherRecommendedProjects, ...additionalProjects];
    }

    return [selectedProject, ...otherRecommendedProjects];
  };

  // 获取显示的项目列表
  const displayProjects = getDisplayProjects();

  // 计算菜单的最佳位置
  const getMenuPosition = () => {
    if (!moreButtonRef.current) {
      return { top: 0, left: 0 };
    }

    const buttonRect = moreButtonRef.current.getBoundingClientRect();
    const menuWidth = 256; // 菜单宽度 (w-64 = 16rem = 256px)
    const menuHeight = 300; // 估计的菜单最大高度
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // 默认位置：按钮下方
    let top = buttonRect.bottom + 8;
    let left = buttonRect.left;

    // 检查右边界：如果菜单会超出右边界，则向左对齐
    if (left + menuWidth > windowWidth) {
      left = Math.max(0, windowWidth - menuWidth);
    }

    // 检查下边界：如果菜单会超出下边界，则显示在按钮上方
    if (top + menuHeight > windowHeight) {
      top = Math.max(0, buttonRect.top - menuHeight - 8);
    }

    return { top, left };
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* 项目快捷按钮 */}
      <div className="flex flex-wrap gap-2 max-w-full">
        {/* 显示选中项目和推荐项目 */}
        {displayProjects.map((project) => (
          <button
            key={project.id}
            type="button"
            onClick={() => handleSelectProject(project)}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 truncate max-w-[120px] ${
              selectedProjectId === project.id
                ? 'bg-blue-500 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-ios-dark-gray6 dark:text-ios-dark-gray2 dark:hover:bg-ios-dark-gray4'
            }`}
            title={project.title} // 添加title属性，鼠标悬停时显示完整名称
          >
            {project.title}
          </button>
        ))}

        {/* 更多项目按钮 */}
        <button
          ref={moreButtonRef}
          type="button"
          onClick={handleMoreClick}
          className="px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-ios-dark-gray6 dark:text-ios-dark-gray2 dark:hover:bg-ios-dark-gray4 flex-shrink-0"
        >
          ...
        </button>
      </div>

      {/* 项目下拉菜单 - 使用Portal渲染到body */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isOpen && (
        <div className="fixed inset-0 z-[9999] bg-transparent" onClick={() => setIsOpen(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed z-[10000] w-64 bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden"
            style={menuMounted ? getMenuPosition() : {}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-80 overflow-y-auto">
              {/* 无项目选项 */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡
                  e.preventDefault(); // 阻止默认行为
                  // 直接调用handleSelectProject，不要使用匿名函数包装
                  handleSelectProject(null);
                }}
                className={`w-full text-left px-4 py-3 text-sm ${
                  selectedProjectId === null
                    ? 'bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue font-medium'
                    : 'text-ios-black dark:text-white hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4'
                }`}
              >
                {t('v3/tasks:quickAdd.noProject')}
              </button>

              {/* 所有项目列表（排除已在外部显示的项目） */}
              {allProjects
                .filter(project => !displayProjects.some(p => p.id === project.id))
                .map((project) => (
                  <button
                    key={project.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // 阻止事件冒泡
                      e.preventDefault(); // 阻止默认行为
                      // 直接调用handleSelectProject，不要使用匿名函数包装
                      handleSelectProject(project);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm ${
                      selectedProjectId === project.id
                        ? 'bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue font-medium'
                        : 'text-ios-black dark:text-white hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4'
                    }`}
                  >
                    {project.title}
                  </button>
                ))}
            </div>
          </motion.div>
        </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};
